// The reader's own 1:1 with the facilitator (D77).
//
// A second, independent thread view beside `Conversation`: same shape of screen — the
// scrollback, the composer, "Read older" — over the reader's own solo thread instead of the
// group's.
//
// Before a group thread exists, this is also where the discussing state's own asks have to
// render: pending actions are D70's plan-wide list, and `Conversation` falls back to this
// screen until the group arrives (see `PlanScreen`), so an ask raised during discussing —
// the move-to-deciding confirmation, an invitation alongside it — would otherwise have
// nowhere to appear. Filtered to this thread's own asks, using the same row and the same
// placement `Conversation` would give them once the group exists. Once the group exists,
// an ask appears in both places at once — accepted placeholder duplication, not a bug.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { IssueEntryRef, IssueView, MessageChoice, Thread } from '../api/contract.js';
import { Composer } from '../components/Composer.js';
import { ThreadRoster } from '../components/ThreadRoster.js';
import { setShownThread } from '../operator/debugConversation.js';
import type { Internals } from '../internals.js';
import { useOpenSuggestion } from '../plan/openSuggestion.js';
import { useUploadChoice } from '../plan/uploadChoice.js';
import { interleave } from '../plan/inline.js';
import { currentTurns, newestTurnId } from '../plan/issue.js';
import type { PlanState } from '../plan/usePlan.js';
import { ListeningBubble } from '../components/ListeningBubble.js';
import { useFollowBottom } from '../plan/useFollowBottom.js';
import { useNewItems } from '../plan/useNewItems.js';
import { useMarkThreadRead } from '../plan/useMarkThreadRead.js';
import { usePrivateThread } from '../plan/usePrivateThread.js';
import { useVoiceContext } from '../voice/VoiceContext.js';

export type PrivateChatProps = {
  client: ApiClient;
  thread: Thread | null;
  state: PlanState;
  internals: Internals;
  label?: string;
  emptyText?: string;
  /** Which room this is, for `ThreadRoster`'s heading. Defaults to the reader's own 1:1. */
  roomKind?: 'private' | 'principals';
  /** See `Conversation.tsx`'s prop of the same name — the mobile decisions strip, handed
   * down so it renders directly above the composer rather than beside the conversation. */
  decisionsStrip?: JSX.Element | null;
  /** The room switcher is showing, so the heading naming this conversation is left out. */
  switcherShown?: boolean;
  /** In an issue conversation: the issue as the reader sees it, and how a card's Undo acts. */
  issue?: {
    readonly view: IssueView | null;
    readonly undo: (entry: IssueEntryRef) => Promise<void>;
  };
};

export function PrivateChat({
  client,
  thread,
  state,
  internals,
  label = 'Private chat',
  emptyText = 'This is between you and Mor. Say anything.',
  roomKind = 'private',
  decisionsStrip,
  switcherShown = false,
  issue,
}: PrivateChatProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const own = usePrivateThread(client, thread, state.snapshot);
  const navigate = useNavigate();
  const [acting, setActing] = useState(false);
  const planId = snapshot?.plan.id ?? '';
  const suggestionOpener = useOpenSuggestion({ client, planId, state, navigate });
  const upload = useUploadChoice({ client, planId, threadId: thread?.id ?? null });
  const turns = useMemo(() => currentTurns(issue?.view ?? null), [issue?.view]);

  // Tells the voice session which conversation this is, and hands it the messages so it can
  // speak the new ones from Mor and from other people. Cleared when this screen goes away or has no composer; the session
  // treats a clear followed at once by another conversation as a switch.
  const voice = useVoiceContext();
  const setVoiceTarget = voice?.setTarget;
  const threadId = thread?.id ?? null;
  // The Debug menu in the top bar reads which conversation is on screen from here.
  useEffect(() => {
    if (roomKind !== 'private' || threadId === null) {
      return undefined;
    }
    setShownThread(threadId);
    return () => setShownThread(null);
  }, [roomKind, threadId]);
  const busy = thread?.facilitatorBusy ?? false;
  const post = own.post;
  const voicePeople = snapshot?.people ?? [];
  const voiceNamed = roomKind === 'principals';
  useEffect(() => {
    if (setVoiceTarget === undefined) {
      return;
    }
    if (threadId === null) {
      setVoiceTarget(null);
      return;
    }
    setVoiceTarget({
      threadId,
      messages: own.messages,
      people: voicePeople,
      named: voiceNamed,
      post,
      facilitatorBusy: busy,
      loaded: own.loaded,
    });
  }, [
    setVoiceTarget,
    threadId,
    own.messages,
    voicePeople,
    voiceNamed,
    own.loaded,
    busy,
    post,
  ]);
  useEffect(() => () => setVoiceTarget?.(null), [setVoiceTarget]);

  // Same rule as `Conversation`, and the same reason the interleaved `items` — not
  // `own.messages` alone — feed `lastKey`: a pending action arriving with no accompanying
  // message has to move it too. Computed unconditionally, ahead of the null check below,
  // because `useFollowBottom` is a hook. `resetKey` is this thread's own id; see
  // `useFollowBottom` for why.
  //
  // An ask carries a `createdAt`, so these never sit permanently at the end the way
  // `Conversation`'s can — but the count is included in `lastKey` anyway, on the same
  // reasoning, rather than leave this screen's computation looking identical while resting
  // on an assumption `Conversation` can't make.
  const asks =
    snapshot === null
      ? []
      : snapshot.pendingActions.filter((action) => action.threadId === thread?.id);
  const items = interleave(own.messages, asks);
  const listening = useVoiceContext();
  const heard = listening?.on === true ? listening.partial : '';
  const lastKey = `${items.length}:${items[items.length - 1]?.key ?? ''}:${heard.length}`;
  const newItems = useNewItems(items, snapshot !== null, thread?.id ?? '');
  const { scroller, atBottom } = useFollowBottom(lastKey, thread?.id ?? '');
  useMarkThreadRead(thread?.id ?? null, own.messages, atBottom, state.markRead);

  if (snapshot === null) {
    return null;
  }

  const { MessageRow, PendingActionRow, FacilitatorState } = internals;
  const newestMessageId = own.messages[own.messages.length - 1]?.id ?? null;
  const standing =
    thread !== null && thread.issue !== null && thread.topic !== null
      ? `/plans/${snapshot.plan.id}/topics/${thread.topic}/issues/${thread.issue}/standing`
      : null;
  // A pressed reply is sent as the reader's own message; "finish" then goes to the summary,
  // or from the first conversation to the home page.
  const suggestions = snapshot.plan.firstConversation?.suggestions ?? [];
  const choose = (choice: MessageChoice): void => {
    if (choice.upload) {
      upload.press();
      return;
    }
    if (choice.suggestion !== undefined) {
      const suggestion = suggestions.find(
        (candidate) => candidate.id === choice.suggestion,
      );
      if (suggestion !== undefined) {
        void suggestionOpener.open(suggestion);
      }
      return;
    }
    setActing(true);
    own.post(choice.text).then(
      () => {
        setActing(false);
        if (choice.finish && standing !== null) {
          navigate(standing, { state: { saved: true } });
        } else if (
          choice.finish &&
          thread !== null &&
          thread.issue === null &&
          thread.topic === null
        ) {
          navigate(`/plans/${snapshot.plan.id}`);
        }
      },
      () => setActing(false),
    );
  };
  // A suggestion's button shows the text Mor wrote for it; one the view no longer lists
  // is hidden.
  const choiceLabel = (choice: MessageChoice): string | null =>
    choice.suggestion === undefined ||
    suggestions.some((candidate) => candidate.id === choice.suggestion)
      ? choice.text
      : null;
  const undo =
    issue === undefined
      ? undefined
      : {
          turns,
          newest: newestTurnId(own.messages),
          busy: acting,
          undo: (entry: IssueEntryRef): void => {
            setActing(true);
            issue.undo(entry).then(
              () => setActing(false),
              () => setActing(false),
            );
          },
        };

  // Undo on an added-issue card removes the issue by hand, then reads the plan again.
  const added = {
    decisions: snapshot.decisions,
    topics: snapshot.topics,
    thread: thread === null ? null : { topic: thread.topic, issue: thread.issue },
    isPrincipal: snapshot.plan.viewerIsPrincipal,
    busy: acting,
    remove: (decision: string): void => {
      setActing(true);
      client
        .changeRecord(snapshot.plan.id, { kind: 'remove_decision', decision })
        .then(() => state.reload())
        .then(
          () => setActing(false),
          () => setActing(false),
        );
    },
  };

  return (
    <section className="conversation" aria-label={label}>
      {upload.input}
      <ThreadRoster
        people={snapshot.people}
        kind={roomKind}
        thread={thread}
        switcherShown={switcherShown}
      />
      <div className="conversation__scroller" ref={scroller}>
        {own.nextBefore === null ? null : (
          <button
            type="button"
            className="button conversation__older"
            onClick={own.loadOlder}
          >
            Read older
          </button>
        )}
        {items.length === 0 && heard === '' ? (
          <p className="conversation__empty">{emptyText}</p>
        ) : (
          <ol className="conversation__items">
            {items.map((item) =>
              item.kind === 'message' ? (
                <MessageRow
                  key={item.key}
                  isNew={newItems.has(item.key)}
                  message={item.message}
                  people={snapshot.people}
                  planId={snapshot.plan.id}
                  showAuthor={roomKind === 'principals'}
                  added={added}
                  {...(undo === undefined ? {} : { issue: undo })}
                  {...(item.message.id === newestMessageId
                    ? {
                        choices: {
                          choose,
                          busy:
                            acting ||
                            own.posting ||
                            upload.busy ||
                            suggestionOpener.opening !== null,
                          label: choiceLabel,
                        },
                      }
                    : {})}
                />
              ) : (
                <PendingActionRow
                  key={item.key}
                  isNew={newItems.has(item.key)}
                  action={item.action}
                  people={snapshot.people}
                  topics={snapshot.topics}
                  resolve={state.resolve}
                />
              ),
            )}
            {heard === '' ? null : <ListeningBubble text={heard} />}
          </ol>
        )}
      </div>

      <div className="conversation__foot">
        <FacilitatorState facilitatorBusy={thread?.facilitatorBusy ?? false} />
        {upload.problem === null ? null : (
          <p className="artifact__problem" role="alert">
            {upload.problem}
          </p>
        )}
        {decisionsStrip ?? null}
        {suggestionOpener.problem === null ? null : (
          <p className="page__problem" role="alert">
            {suggestionOpener.problem}
          </p>
        )}
        {thread === null ? null : (
          <Composer
            posting={own.posting}
            sendFailed={own.sendFailed}
            onSend={own.post}
            threadId={thread.id}
          />
        )}
      </div>
    </section>
  );
}
